import React, { useState, useEffect, useMemo } from 'react';
import { Box, useTheme, Typography, Button, Card, CardContent, useMediaQuery, TextField } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Search as SearchIcon } from '@mui/icons-material';
import { tokens } from '../../theme';
import { useNavigate, useParams } from "react-router-dom";
import useAccountantService from '../../api/services/AccountantService';
import useStudentService from '../../api/services/studentService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { DataGrid } from '@mui/x-data-grid';
import * as image from '../../assets';
import { toast } from 'react-hot-toast';

const AccountantsDataTermsByStudentId = () => {
  const { studentId } = useParams();
  const theme = useTheme();
  const borderColor = theme.palette.mode === 'dark' ? 'white' : 'black';
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [terms, setTerms] = useState([]);
  const [studentData, setStudentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const { getAllTermsByStudentId } = useAccountantService();
  const { loadStudentDetails } = useStudentService();

  useEffect(() => {
    const fetchData = async () => {
      if (studentId) {
        setIsLoading(true);
        try {
          // Load student details
          const student = await loadStudentDetails(studentId);
          setStudentData(student);

          // Load terms and filter PUBLISHED and ACTIVE status
          const data = await getAllTermsByStudentId(studentId);
          const filteredTerms = (data || []).filter(term => term.status === 'PUBLISHED' || term.status === 'ACTIVE');
          const sortedData = filteredTerms.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setTerms(sortedData);
        } catch (error) {
          console.error('Error fetching data:', error);
          toast.error('Failed to load terms data');
          setTerms([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const handleViewAccounts = (termId, termName) => {
    if (!studentId || !termId) {
      console.error('Missing studentId or termId for navigation');
      return;
    }
    // Navigate to view account details for this student and term
    navigate(`/student-accounts-data/${termId}/${studentId}`, {
      state: { termId, studentId, termName },
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Filter terms based on search query
  const filteredTerms = useMemo(() => {
    if (!searchQuery.trim()) {
      return terms;
    }
    const query = searchQuery.toLowerCase().trim();
    return terms.filter(term =>
      term.termName?.toLowerCase().includes(query) ||
      term.status?.toLowerCase().includes(query)
    );
  }, [terms, searchQuery]);

  const columns = [
    {
      field: 'termName',
      headerName: 'Jina la Muhula',
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 60,
    },
    {
      field: 'action',
      headerName: 'Action',
      minWidth: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const handleClick = () => {
          handleViewAccounts(params.row.id, params.row.termName);
        };

        return (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={handleClick}
          >
            Tazama Michango & Ada
          </Button>
        );
      },
    },
  ];

  return (
    <Box sx={{ ml: '20px', mr: '20px' }}>


      {/* Header Banner */}
      <Box
        height="auto"
        display="flex"
        justifyContent="space-between"
        border={`1px solid ${borderColor}`}
        mb={2}
        flexWrap="wrap"
      >
        {/* Left */}
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          borderRight={{ xs: 'none', md: `1px solid ${borderColor}` }}
          p={1}
          justifyContent="center"
        >
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center">
            <Box
              component="img"
              src={image.visigalogo}
              alt="logo"
              sx={{ height: 80, width: 80, mb: { xs: 1, md: 0 }, mr: { xs: 0, md: 2 } }}
            />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.7rem', sm: '1.1rem', md: '1.5rem' } }}
            >
              St Mary&apos;s Junior Seminary (VISIGA SEMINARY)
            </Typography>
          </Box>
        </Box>

        {/* Center */}
        <Box
          flex={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
          borderRight={{ xs: 'none', md: `1px solid ${borderColor}` }}
          p={2}
          textAlign="center"
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.4rem' } }}
          >
            MICHANGO & ADA YA KILA MUHULA
          </Typography>
        </Box>

        {/* Right */}
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          p={1}
          justifyContent="center"
        >
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center">
            <Box
              component="img"
              src={image.cathedral5}
              alt="arch"
              sx={{ height: 80, width: 80, mb: { xs: 1, md: 0 }, mr: { xs: 0, md: 2 } }}
            />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.7rem', sm: '1.1rem', md: '1.5rem' } }}
            >
              Seminary under Archdiocese of Dar es Salaam
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Student Info */}
      {studentData && (
        <Box mb={2}>
          <Typography variant="h5" gutterBottom>
            Jina la mseminari: <strong>{studentData.studentName || 'N/A'}</strong>
          </Typography>
        </Box>
      )}

      {/* Terms List */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" align="start">
            ORODHA YA MUHULA - (MICHANGO & ADA)
          </Typography>

          {/* Search Field */}
          <TextField
            placeholder="Tafuta kwa jina la muhula "
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{
              minWidth: { xs: '100%', sm: '300px' },
              maxWidth: { xs: '100%', sm: '400px' },
            }}
          />
        </Box>

        {isLoading ? (
          <LoadingSpinner message="Loading terms..." height="250px" />
        ) : filteredTerms.length > 0 ? (
          <>
            {isSmallScreen ? (
              // Card view for small screens
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredTerms.map((term) => (
                  <Card
                    key={term.id}
                    sx={{
                      border: `1px solid ${borderColor}`,
                      borderRadius: 1,
                      boxShadow: 1,
                    }}
                  >
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {/* Term Name */}
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem', lineHeight: 1.2 }}>
                            {term.termName || 'N/A'}
                          </Typography>
                        </Box>
                        
                        {/* Status and Button in one row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                              Status
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.65rem',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                display: 'inline-block',
                                mt: 0.25
                              }}
                            >
                              {term.status || 'N/A'}
                            </Typography>
                          </Box>
                          
                          <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => handleViewAccounts(term.id, term.termName)}
                            sx={{ fontSize: '0.75rem', py: 0.5, minHeight: '32px', whiteSpace: 'nowrap' }}
                          >
                            Tazama Michango & Ada
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              // DataGrid for larger screens
              <Box sx={{ height: 500, width: '100%', mt: 2 }}>
                <DataGrid
                  rows={filteredTerms}
                  columns={columns}
                  getRowId={(row) => row.id}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{
                    '& .MuiDataGrid-cell:hover': {
                      cursor: 'default',
                    },
                  }}
                />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="textSecondary">
              {searchQuery.trim()
                ? 'No terms found matching your search.'
                : 'No published or active terms with account data available for this student.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AccountantsDataTermsByStudentId;

